/**
 * Penyusunan permintaan — MURNI, tanpa `fetch` dan tanpa `config`.
 *
 * Dipisahkan dari `client.ts` bukan demi kerapian, melainkan supaya aturan yang benar-benar
 * penting dapat diuji tanpa DOM maupun jaringan: apakah `Idempotency-Key` benar-benar ikut pada
 * setiap mutasi, dan apakah token serta organisasi dikirim pada bentuk yang dibaca server.
 * `config.ts` membaca `import.meta.env` milik Vite yang tidak ada di lingkungan pengujian Node.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AuthState {
  readonly accessToken: string;
  readonly organizationId: string;
}

export interface HeaderOptions {
  readonly hasBody: boolean;
  /** Wajib untuk seluruh mutasi absensi. Tanpa ini server menolak dengan `WF-900`. */
  readonly idempotencyKey?: string;
}

/**
 * `organizationId` dikirim sebagai USULAN lewat `x-organization-id`; server memverifikasinya ke
 * HBS sebelum memakainya. Mengirimkannya bukan berarti memilikinya.
 */
export function buildHeaders(
  auth: AuthState | null,
  options: HeaderOptions,
): Record<string, string> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.hasBody) {
    headers['content-type'] = 'application/json';
  }
  if (auth) {
    headers.authorization = `Bearer ${auth.accessToken}`;
    headers['x-organization-id'] = auth.organizationId;
  }
  if (options.idempotencyKey !== undefined) {
    headers['idempotency-key'] = options.idempotencyKey;
  }
  return headers;
}

/** Bentuk galat yang dipahami layar, dari badan respons apa pun — termasuk yang bukan JSON. */
export function toApiError(status: number, body: unknown, correlationId: string | null): ApiError {
  const envelope = body as { error?: { code?: unknown; message?: unknown } } | null;
  const code = typeof envelope?.error?.code === 'string' ? envelope.error.code : 'ERROR';
  const message =
    typeof envelope?.error?.message === 'string'
      ? envelope.error.message
      : `Permintaan gagal (${status}).`;
  return new ApiError(status, code, message, correlationId);
}

/** Rentang tanggal untuk query riwayat, sebagai `YYYY-MM-DD`. */
export function attendanceRangeQuery(from: string, to: string): string {
  return `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
}
